import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth, getDefaultRedirect } from '@/hooks/useAuth';
import { RoleLoginShell } from '@/components/auth/RoleLoginShell';
import { ROLE_LOGIN_THEMES } from '@/components/auth/roleLoginThemes';
import { requestOtp, verifyOtp, phoneRegister } from '@/api/auth';

const THEME = ROLE_LOGIN_THEMES["login"];

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, applyAuth, isAuthenticated, user } = useAuth();
  const redirectTo = searchParams.get("redirect") || "";
  const [authMode, setAuthMode] = useState<'email' | 'phone'>('email');
  // Email login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Phone OTP state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("91");
  const [otpStep, setOtpStep] = useState<'phone' | 'otp' | 'register'>('phone');
  const [otpCode, setOtpCode] = useState("");
  const [otpName, setOtpName] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(redirectTo || getDefaultRedirect(user.roles), { replace: true });
    }
  }, [isAuthenticated, user, navigate, redirectTo]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError("");
    try {
      const response = await login(email.trim(), password);
      navigate(redirectTo || getDefaultRedirect(response.user.roles), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber.trim() || phoneNumber.length < 10) {
      setError("Please enter a valid mobile number");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await requestOtp(phoneNumber, countryCode, 'login');
      if (data.otp) setDevOtp(data.otp);
      setResendTimer(60);
      setOtpStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length < 4) {
      setError("Please enter the complete OTP");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const data = await verifyOtp(phoneNumber, otpCode, countryCode, 'login');
      if (!data.verified) {
        throw new Error("Invalid OTP. Please try again.");
      }
      if (data.user_exists && data.auth) {
        const response = applyAuth(data.auth);
        navigate(redirectTo || getDefaultRedirect(response.user.roles), { replace: true });
      } else {
        // New user — show name registration step
        setOtpStep('register');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpName.trim()) {
      setError("Please enter your name");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const auth = await phoneRegister(phoneNumber, countryCode, otpName.trim());
      const response = applyAuth(auth);
      navigate(redirectTo || getDefaultRedirect(response.user.roles), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setError("");
    setLoading(true);
    try {
      const data = await requestOtp(phoneNumber, countryCode, 'login');
      if (data.otp) setDevOtp(data.otp);
      setResendTimer(60);
      setOtpCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const switchToPhone = () => {
    setAuthMode('phone');
    setError("");
    setOtpStep('phone');
    setOtpCode("");
    setDevOtp("");
  };

  const switchToEmail = () => {
    setAuthMode('email');
    setError("");
  };

  const meta = (
    <>
      <p className="rl__meta-row">
        Don&apos;t have an account? <Link to="/register">Create one</Link>
      </p>
      <p className="rl__meta-row">
        Registering an academy? <Link to="/register/academy">Register Academy</Link>
      </p>
      <p className="rl__meta-row">
        Already registered? <Link to="/registration/status">Check status</Link>
      </p>
    </>
  );

  return (
    <RoleLoginShell theme={THEME} meta={meta}>
      <div className="rl__tabs" role="tablist" aria-label="Sign-in method">
        <button
          type="button"
          role="tab"
          className="rl__tab"
          aria-pressed={authMode === 'email'}
          onClick={switchToEmail}
        >
          Email
        </button>
        <button
          type="button"
          role="tab"
          className="rl__tab"
          aria-pressed={authMode === 'phone'}
          onClick={switchToPhone}
        >
          Mobile · OTP
        </button>
      </div>

      {error && <div className="rl__error" role="alert">{error}</div>}

      {authMode === 'email' && (
        <form className="rl__form" onSubmit={handleSubmit}>
          <div className="rl__field">
            <label className="rl__label" htmlFor="login-email">Email</label>
            <input id="login-email" className="rl__input" type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
              required autoFocus autoComplete="email" />
          </div>
          <div className="rl__field">
            <label className="rl__label" htmlFor="login-password">Password</label>
            <div className="rl__password-wrap">
              <input id="login-password" className="rl__input"
                type={showPassword ? "text" : "password"} value={password}
                onChange={e => setPassword(e.target.value)} placeholder="Enter your password"
                required autoComplete="current-password" />
              <button type="button" className="rl__password-toggle"
                onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <p className="rl__meta-row">
            <Link to="/forgot-password">Forgot password?</Link>
          </p>
          <button type="submit" className="rl__submit" disabled={loading || !email.trim() || !password}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Signing in...</> : "Sign In"}
          </button>
        </form>
      )}

      {/* Phone OTP — number entry */}
      {authMode === 'phone' && otpStep === 'phone' && (
        <form className="rl__form" onSubmit={handleRequestOtp}>
          <div className="rl__field">
            <label className="rl__label" htmlFor="login-phone">Mobile Number</label>
            <div className="rl__phone-group">
              <select
                id="login-country-code"
                className="rl__select"
                value={countryCode}
                onChange={e => setCountryCode(e.target.value)}
                aria-label="Country code"
              >
                <option value="91">+91</option>
                <option value="1">+1</option>
                <option value="44">+44</option>
                <option value="61">+61</option>
                <option value="971">+971</option>
              </select>
              <input
                type="tel"
                id="login-phone"
                className="rl__input"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter mobile number"
                autoComplete="tel-national"
                required
                maxLength={15}
              />
            </div>
          </div>
          <button type="submit" className="rl__submit" disabled={loading}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Sending OTP...</> : "Send OTP"}
          </button>
        </form>
      )}

      {/* Phone OTP — code entry */}
      {authMode === 'phone' && otpStep === 'otp' && (
        <form className="rl__form" onSubmit={handleVerifyOtp}>
          <p className="rl__otp-note">
            OTP sent to +{countryCode} {phoneNumber}
          </p>
          {devOtp && (
            <p className="rl__otp-dev">
              Development OTP <strong>{devOtp}</strong>
            </p>
          )}
          <div className="rl__field">
            <label className="rl__label" htmlFor="login-otp">Enter OTP</label>
            <input
              type="text"
              id="login-otp"
              className="rl__input rl__otp-input"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="······"
              required
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </div>
          <button type="submit" className="rl__submit" disabled={loading}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Verifying...</> : "Verify OTP"}
          </button>
          <div className="rl__otp-actions">
            <button
              type="button"
              className="rl__link-btn"
              onClick={handleResendOtp}
              disabled={resendTimer > 0 || loading}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
            </button>
            <button
              type="button"
              className="rl__link-btn"
              onClick={() => { setOtpStep('phone'); setOtpCode(''); setDevOtp(''); setError(''); }}
            >
              Change number
            </button>
          </div>
        </form>
      )}

      {/* Phone OTP — new-user name capture */}
      {authMode === 'phone' && otpStep === 'register' && (
        <form className="rl__form" onSubmit={handlePhoneRegister}>
          <p className="rl__otp-note">
            No account found for +{countryCode} {phoneNumber}. Enter your name to create one.
          </p>
          <div className="rl__field">
            <label className="rl__label" htmlFor="login-otp-name">Your Name</label>
            <input
              type="text"
              id="login-otp-name"
              className="rl__input"
              value={otpName}
              onChange={e => setOtpName(e.target.value)}
              placeholder="Enter your full name"
              autoComplete="name"
              required
            />
          </div>
          <button type="submit" className="rl__submit" disabled={loading}>
            {loading ? <><Loader2 size={16} className="rl__spin" /> Creating account...</> : "Create account & sign in"}
          </button>
        </form>
      )}
    </RoleLoginShell>
  );
}
